const React = require('react');
const T = require('prop-types');
const { useAppSelector } = require('utils/redux');
const { default: Styled } = require('styled-components');

const { default: Button } = require('@mui/material/Button');
const { default: FAB } = require('@mui/material/Fab');
const { default: AddIcon } = require('@mui/icons-material/Add');
const { default: RemoveIcon } = require('@mui/icons-material/Remove');
const { default: List } = require('@mui/material/List');
const { default: ListItem } = require('@mui/material/ListItem');
const { default: useMediaQuery } = require('@mui/material/useMediaQuery');
const { default: RightArrowIcon } = require('@mui/icons-material/ChevronRight');

const { context } = require('app-context');
const ClassListItem = require('routes/app/classes/components/ClassListItem');
const { default: Classes } = require('./ManageGroupsDialog/styles.scss');
const { useTheme } = require('@mui/material/styles');
const { getGroupLastMessage, gotoGroupLink } = require('utils/groups');
const FuzzyFilter = require('utils/fuzzyFilter');
const LoadingScreen = require('components/LoadingScreen');

const { useEffect, useState, useRef } = React;

const {
    selectors: { all: selectors }
} = context;

const internals = {};

const ManageGroupsContents = (props) => {

    const {
        fetchGroups,
        onClickAddToGroup,
        onClickRemoveFromGroup,
        currentCommunityUnreadCounts,
        groupSearchResults,
        myCurrentGroups,
        currentGroups,
        allGroups,
        push,
        user,
        isMe,
        searchText,
        sortGroups,
        debug,
        showChatDetails,
        usersNameMap,
        currentUserId,
        rolePermissions,
        usePageHeading
    } = props;

    const {
        styles,
        ListWrapper,
        SectionCard,
        SectionTitle
    } = internals;

    const wide = useMediaQuery('(min-width: 600px)');
    const channelLastMessages = useAppSelector(selectors.getChannelLastMessages);

    const [groupsAreFetched, setGroupsAreFetched] = useState(!!isMe);
    const isFetchingGroups = useRef(false);

    const [localCurrentGroups, setLocalCurrentGroups] = useState(currentGroups || []);

    const displayCurrentGroups = isMe ? currentGroups : localCurrentGroups;

    const { id: userId } = user;

    // Fetch on init for managed-user dialogs. The /app/classes route resolves
    // current-user groups before rendering, so avoid a second page-level loader there.
    useEffect(() => {

        if (isMe) {
            if (!groupsAreFetched) {
                setGroupsAreFetched(true);
            }

            return undefined;
        }

        const attemptFetch = async () => {

            isFetchingGroups.current = true;

            await fetchGroups({ managedUserId: userId });

            setGroupsAreFetched(true);
        };

        if (!groupsAreFetched && !isFetchingGroups.current) {
            attemptFetch();
        }
    }, [
        isMe,
        userId,
        fetchGroups,
        groupsAreFetched,
        setGroupsAreFetched
    ]);

    const handleAddToGroup = (group) => {

        onClickAddToGroup(group).then((result) => {

            if (result !== undefined) {
                setLocalCurrentGroups((prev) => [...prev, { id: group.id, name: group.name }]);
            }
        });
    };

    const handleRemoveFromGroup = (group) => {

        onClickRemoveFromGroup(group).then((result) => {

            if (result !== undefined) {
                setLocalCurrentGroups((prev) => prev.filter(({ id }) => id !== group.id));
            }
        });
    };

    const theme = useTheme();

    const canViewUnjoinedGroups = !!rolePermissions?.canSeeChatMessagesNoJoin;
    const getUnjoinedGroupLink = (group) => canViewUnjoinedGroups ? `/app/classes/${group.id}` : undefined;

    if (!groupsAreFetched) {
        return <LoadingScreen variant='content' />;
    }

    const middleActionButton = wide ? (
        <Button
            color='secondary'
            variant='contained'
            size='small'
            style={{ ...styles.joinButton, background: theme.palette.secondary.main }}
            className={Classes.joinButton}
            data-focus-outline='radius:40,padding:3,zIndex:2'
            disableElevation
            sx={{
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
                '&.Mui-focusVisible': { boxShadow: 'none' }
            }}
        >
            {isMe ? 'Join' : 'Add'}
        </Button>
    ) : (
        <FAB
            color='secondary'
            size='small'
            style={{ ...styles.joinFab, background: theme.palette.secondary.main }}
            data-focus-outline='radius:40,padding:3,zIndex:2'
            sx={{
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
                '&.Mui-focusVisible': { boxShadow: 'none' }
            }}
        >
            <AddIcon />
        </FAB>
    );

    const remappedCurrentGroups = displayCurrentGroups.map((userGroup) => {

        const fullGroupData = allGroups?.find(({ id }) => id === userGroup.id);
        return { ...userGroup, ...fullGroupData };
    }) || [];

    const userCurrentGroups = sortGroups
        ? sortGroups(remappedCurrentGroups, {
            lastMessages: channelLastMessages,
            usersNameMap,
            currentUserId
        }) // Sort based on dropdown for My Groups page
        : remappedCurrentGroups.sort((a, b) => a.name.localeCompare(b.name)); // Default to alphabetical

    const searchedGroupsSorted = searchText
        ? allGroups.filter((group) => {

            if (groupSearchResults?.find(({ id }) => id === group.id)) {
                return false;
            }

            return FuzzyFilter(searchText, group.name);
        })
            .concat(groupSearchResults || []).sort((a, b) => {

                const alreadyInGroupA = !!displayCurrentGroups.find((userGroup) => userGroup.id === a.id);
                const alreadyInGroupB = !!displayCurrentGroups.find((userGroup) => userGroup.id === b.id);

                return (Number(alreadyInGroupB) - Number(alreadyInGroupA)) || a.name.localeCompare(b.name);
            })
        : [];

    const joinableGroups = !allGroups
        ? []
        : allGroups
            .filter((group) => !displayCurrentGroups.find((userGroup) => userGroup.id === group.id));

    return (
        <ListWrapper $usePageHeading={usePageHeading}>
            <List>
                <ListItem className={Classes.center}>
                    <div className={Classes.center} style={{ width: '100%' }}>
                        {(!searchText && !!allGroups?.length && !userCurrentGroups?.length) && (
                            <SectionTitle as='h2'>
                                {isMe ? 'Join your first group below!' : `${user?.firstName} isn't in any groups`}
                            </SectionTitle>
                        )}
                        {!searchText && !!userCurrentGroups?.length && <SectionTitle as={usePageHeading ? 'h1' : 'h2'}>
                            {isMe ? 'Your Groups' : `${user?.firstName}'s Groups`}
                        </SectionTitle>}
                        {!!searchText && searchedGroupsSorted.length === 0 && (<SectionTitle as='h2'>
                            No groups found.
                        </SectionTitle>)}
                        {!!searchText && !!searchedGroupsSorted.length && (<SectionTitle as='h2'>
                            Search Results
                        </SectionTitle>)}
                        <SectionCard>
                            <div style={{ width: '100%' }}>
                                {(!searchText && !!userCurrentGroups?.length) ? userCurrentGroups?.map((group) => {

                                const unreadCount = internals.getUnreadCount(group, currentCommunityUnreadCounts);
                                const lastMessage = getGroupLastMessage(group, channelLastMessages);

                                return (
                                    <ClassListItem
                                        data-focus-outline='zIndex:1,radius:16,padding:3'
                                        className='nearpeer-group-list-item'
                                        unreadCount={unreadCount}
                                        lastMessage={lastMessage}
                                        key={internals.getGroupListItemKey(group, unreadCount)}
                                        link={gotoGroupLink(group, myCurrentGroups)}
                                        showPrivate
                                        group={group}
                                        titleFontSize={17}
                                        action={isMe ? onClickRemoveFromGroup : handleRemoveFromGroup}
                                        middleIcon={isMe ? <RightArrowIcon /> : null}
                                        showChatDetails={showChatDetails}
                                        usersNameMap={usersNameMap}
                                        currentUserId={currentUserId}
                                        middleActionButton={isMe ? null : wide ? (
                                            <Button
                                                color='secondary'
                                                variant='contained'
                                                size='small'
                                                style={{ ...styles.joinButton, background: theme.palette.secondary.main }}
                                                className={Classes.joinButton}
                                                data-focus-outline='radius:40,padding:3,zIndex:2'
                                                disableElevation
                                                sx={{
                                                    boxShadow: 'none',
                                                    '&:hover': { boxShadow: 'none' },
                                                    '&.Mui-focusVisible': { boxShadow: 'none' }
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        ) : (
                                            <FAB
                                                color='secondary'
                                                size='small'
                                                style={{ ...styles.joinFab, background: theme.palette.secondary.main }}
                                                data-focus-outline='radius:40,padding:3,zIndex:2'
                                                sx={{
                                                    boxShadow: 'none',
                                                    '&:hover': { boxShadow: 'none' },
                                                    '&.Mui-focusVisible': { boxShadow: 'none' }
                                                }}
                                            >
                                                <RemoveIcon />
                                            </FAB>
                                        )}
                                        style={{ textAlign: 'left' }}
                                        push={push}
                                        displayDetails
                                        truncateDetails
                                        debug={debug}
                                    />
                                );
                            }) : null}
                            </div>
                        </SectionCard>
                        {!searchText && !!userCurrentGroups?.length && (
                            <SectionTitle $hasTopLevelSpacing>
                                {joinableGroups.length ? 'Groups To Explore' : 'All Available Groups Joined'}
                            </SectionTitle>
                        )}
                        {!searchText && !!joinableGroups.length ? (
                            <SectionCard $compact>
                                {joinableGroups.map((group) => {

                            const unreadCount = internals.getUnreadCount(group, currentCommunityUnreadCounts);
                            const lastMessage = getGroupLastMessage(group, channelLastMessages);

                            return (
                                <ClassListItem
                                    data-focus-outline='zIndex:1,radius:16,padding:3'
                                    className='nearpeer-group-list-item'
                                    unreadCount={unreadCount}
                                        lastMessage={lastMessage}
                                    key={internals.getGroupListItemKey(group, unreadCount)}
                                    link={getUnjoinedGroupLink(group)}
                                    showPrivate
                                    group={group}
                                    titleFontSize={17}
                                    action={isMe ? onClickAddToGroup : handleAddToGroup}
                                    middleActionButton={middleActionButton}
                                    style={{ textAlign: 'left' }}
                                    push={push}
                                    displayDetails
                                    truncateDetails
                                    debug={debug}
                                    showChatDetails={showChatDetails}
                                    usersNameMap={usersNameMap}
                                    currentUserId={currentUserId}
                                />
                            );
                                })}
                            </SectionCard>
                        ) : null}

                        {!!searchText && searchedGroupsSorted.length ? (
                            <SectionCard>
                                {searchedGroupsSorted.map((group) => {

                            const alreadyInGroup = displayCurrentGroups.find((userGroup) => userGroup.id === group.id);
                            const unreadCount = internals.getUnreadCount(group, currentCommunityUnreadCounts);
                            const lastMessage = getGroupLastMessage(group, channelLastMessages);

                            return alreadyInGroup ? (
                                <ClassListItem
                                    className='nearpeer-group-list-item'
                                    unreadCount={unreadCount}
                                        lastMessage={lastMessage}
                                    key={internals.getGroupListItemKey(group, unreadCount)}
                                    displayDetails
                                    truncateDetails
                                    showPrivate
                                    group={group}
                                    titleFontSize={17}
                                    link={gotoGroupLink(group, myCurrentGroups)}
                                    data-focus-outline='zIndex:1,radius:5'
                                    middleIcon={<RightArrowIcon />}
                                    push={push}
                                    debug={debug}
                                    showChatDetails={showChatDetails}
                                    usersNameMap={usersNameMap}
                                    currentUserId={currentUserId}
                                />
                            ) : (
                                <ClassListItem
                                    data-focus-outline='zIndex:1,radius:16,padding:3'
                                    className='nearpeer-group-list-item'
                                    showUnreadBadge
                                    unreadCount={unreadCount}
                                        lastMessage={lastMessage}
                                    displayDetails
                                    truncateDetails
                                    showPrivate
                                    key={internals.getGroupListItemKey(group, unreadCount)}
                                    link={getUnjoinedGroupLink(group)}
                                    group={group}
                                    titleFontSize={17}
                                    action={isMe ? onClickAddToGroup : handleAddToGroup}
                                    middleActionButton={middleActionButton}
                                    push={push}
                                    debug={debug}
                                    showChatDetails={showChatDetails}
                                    usersNameMap={usersNameMap}
                                    currentUserId={currentUserId}
                                />
                            );
                                })}
                            </SectionCard>
                        ) : null}
                    </div>
                </ListItem>
            </List>
        </ListWrapper>
    );
};

ManageGroupsContents.propTypes = {
    fetchGroups: T.func,
    onClickAddToGroup: T.func,
    onClickRemoveFromGroup: T.func,
    currentCommunityUnreadCounts: T.object,
    myCurrentGroups: T.arrayOf(T.shape({ id: T.any.isRequired, name: T.string })) || [],
    currentGroups: T.arrayOf(T.shape({ id: T.any.isRequired, name: T.string })) || [],
    user: T.shape({ id: T.any.isRequired, firstName: T.string }),
    groupSearchResults: T.arrayOf(T.shape({ id: T.any.isRequired })),
    allGroups: T.arrayOf(T.shape({ id: T.any.isRequired, name:T.string })),
    push: T.func,
    isMe: T.bool,
    searchText: T.string,
    sortGroups: T.func,
    debug: T.bool,
    showChatDetails: T.bool,
    usersNameMap: T.object,
    currentUserId: T.number,
    rolePermissions: T.shape({
        canSeeChatMessagesNoJoin: T.bool
    }),
    usePageHeading: T.bool
};

module.exports = ManageGroupsContents;

internals.getUnreadCount = (group = {}, counts = {}) => {

    const keys = [
        group?.channel?.sid,
        group?.conversation?.sid,
        group?.sid,
        group?.id
    ].filter(Boolean);

    const foundCount = keys.map((key) => counts?.[key]?.count)
        .find((count) => typeof count === 'number');

    return foundCount || 0;
};

internals.getGroupListItemKey = (group = {}, unreadCount = 0) => {

    return [
        group.id || group.sid || 'unknown-group',
        unreadCount
    ].join('::');
};

internals.styles = {
    joinButton: {
        width: 'auto',
        margin: 0,
        boxShadow: 'none'
    },
    joinFab: {
        height: 36,
        width: 36,
        minWidth: 36,
        margin: 0,
        boxShadow: 'none'
    }
};

internals.ListWrapper = Styled.div`
    width: 100%;
    max-width: 560px;
    margin: ${({ $usePageHeading }) => $usePageHeading ? '-8px' : '-12px'} auto 0;
    padding: 0 20px 28px;
    box-sizing: border-box;

    ul,
    li,
    h1,
    h3 {
        margin-top: 0;
    }

    .MuiList-root > .MuiListItem-root {
        padding: 0;
    }

    @media screen and (max-width: 600px) {
        padding-right: 20px;
        padding-left: 20px;
    }
`;


internals.SectionCard = Styled.div`
    width: 100%;
    box-sizing: border-box;
    padding: 0;
    background: transparent;
`;


internals.SectionTitle = Styled.h3`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    width: fit-content;
    margin: ${({ $hasTopLevelSpacing }) => $hasTopLevelSpacing ? 28 : 16}px auto 16px !important;
    padding: 0 12px;
    border-radius: 999px;
    background: rgba(18, 39, 79, 0.06);
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 18px;
    line-height: 22px;
    font-weight: 800;
    letter-spacing: -0.015em;
    text-align: center;
`;
